import { useEffect, useState } from 'react';
import { Box, Flex, Grid, GridProps, VStack, Text } from '@chakra-ui/react';
import Buttons from '../Buttons';
import { ServiceOrderRequestProps } from '../../types';
import { mapContactButtonsProps } from '../../lib/resolvers';
import Heading from '../Heading';
import { useForestExpert, useTranslation } from '../../lib/hooks.context';

const Column = (props: GridProps) => (
  <Grid
    alignContent="baseline"
    gridTemplateColumns="100%"
    gridGap="5"
    flex={['0 0 100%', null, null, '0 0 calc(33.3333%)']}
    {...props}
  />
);

const ServiceOrderRequest = ({
  title,
  content,
  contact,
}: ServiceOrderRequestProps) => {
  const { forestExpert } = useForestExpert();
  const t = useTranslation();
  const [{ email, phone }, setState] = useState<{
    email?: string;
    phone?: string;
  }>({ email: contact?.email, phone: contact?.phone });

  useEffect(() => {
    // dont access forestExpert directly in server side
    if (forestExpert && email !== forestExpert.email) {
      setState((s) => ({
        ...s,
        email: forestExpert.email,
        phone: forestExpert.phone,
      }));
    }
  }, [forestExpert, email]);

  return (
    <Box
      id="service-request"
      bg="greenLight"
      borderRadius="lg"
      px={[4, 6, 10]}
      py={[6, 10]}
      data-test-id="block-service-request"
    >
      <Heading as="h3" variant="h5" mb={[4, 6, 8]}>
        {title}
      </Heading>
      <Flex gridColumnGap={5} gridRowGap={10} flexWrap="wrap">
        {content?.map(({ heading, text, buttons }) => (
          <Column
            key={heading?.title}
            pr={[null, null, null, 5]}
            borderRightWidth={[null, null, null, '1px']}
            borderRightStyle={[null, null, null, 'solid']}
            borderColor="highlightColor"
            flex={[
              '0 0 100%',
              null,
              null,
              '0 0 calc(33.3333% - var(--mhy-space-5) * 2 / 3)',
            ]}
          >
            {heading && (
              <Heading as="h2" variant="h4">
                {heading.title}
              </Heading>
            )}
            {text && <Text>{text}</Text>}
            {buttons && <Buttons buttons={buttons} size="large" />}
          </Column>
        ))}
        {contact && (
          <Column
            flex={[
              '0 0 100%',
              null,
              null,
              '0 0 calc(33.3333% - var(--mhy-space-5) * 2 / 3)',
            ]}
          >
            {contact.title && (
              <Heading as="h2" variant="h4">
                {contact.title}
              </Heading>
            )}
            {contact.text ? <Text>{contact.text}</Text> : null}
            {email !== contact?.email ? (
              <Text>{t('your_forest_expert')}</Text>
            ) : null}
            <Buttons
              buttons={mapContactButtonsProps(email, phone)}
              size="large"
              as={VStack}
              alignItems="stretch"
              width="max-content"
              sx={{ 'a, span': { width: '100%' } }}
            />
          </Column>
        )}
      </Flex>
    </Box>
  );
};

export default ServiceOrderRequest;
